
import React from 'react';
import { Button } from "@/components/ui/button";
import { LayoutGrid, LayoutList, Search, Trash2, Copy } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { DownloadButton } from "./DownloadButton";

interface VideoSelectionControlsProps {
  selected: string[];
  onSelectAll: (selectAll: boolean) => void;
  isDownloading: boolean;
  totalVideos: number;
  viewMode: 'grid' | 'table';
  onViewModeChange: (mode: 'grid' | 'table') => void;
  onDownloadAll: () => void;
}

export const VideoSelectionControls: React.FC<VideoSelectionControlsProps> = ({
  selected,
  onSelectAll,
  isDownloading,
  totalVideos,
  viewMode,
  onViewModeChange,
  onDownloadAll
}) => {
  const isMobile = useIsMobile();
  
  return (
    <div className="flex flex-col space-y-4 md:space-y-0 md:flex-row md:items-center justify-between py-4 border-t border-b border-gray-800">
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <input 
            type="checkbox" 
            className="h-4 w-4 rounded border-gray-600 bg-transparent accent-[#00E5FF]" 
            checked={selected.length === totalVideos && totalVideos > 0} 
            onChange={(e) => onSelectAll(e.target.checked)} 
            disabled={isDownloading || totalVideos === 0} 
          />
          <span className="text-sm text-gray-300">Select All</span>
        </div>
        
        <div className="h-4 border-l border-gray-700"></div>
        
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost"
            size="sm"
            className="text-gray-300 hover:text-white hover:bg-gray-800"
            disabled={selected.length === 0 || isDownloading}
          >
            <Trash2 className="h-4 w-4" />
            <span className="hidden md:inline ml-2">Delete</span>
          </Button>
          
          <Button 
            variant="ghost"
            size="sm"
            className="text-gray-300 hover:text-white hover:bg-gray-800"
            disabled={selected.length === 0}
          >
            <Copy className="h-4 w-4" />
            <span className="hidden md:inline ml-2">Copy Links</span>
          </Button>
        </div>
      </div>
      
      <div className="flex items-center gap-3">
        <div className="relative flex-1 md:w-64">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
          <input 
            type="text" 
            placeholder="Search videos..." 
            className="w-full pl-10 pr-4 py-2 bg-gray-800/50 border border-gray-700 rounded-md text-sm text-white placeholder:text-gray-500" 
          />
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onViewModeChange('grid')}
            className={viewMode === 'grid' ? "bg-gray-700 text-white border-gray-600" : "text-gray-400 border-gray-700 hover:bg-gray-700/50"}
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onViewModeChange('table')}
            className={viewMode === 'table' ? "bg-gray-700 text-white border-gray-600" : "text-gray-400 border-gray-700 hover:bg-gray-700/50"}
          >
            <LayoutList className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};
